import { IconProp } from "utils/iconMapping";
import { IBase } from "./Base";
import { EUserSeniority, IUser } from "./User.types";

export enum EEntity {
  AREA = "AREA",
  DIVISION = "DIVISION",
  ROLES = "ROLES",
  SKILL = "SKILL",
  APPRECIATION = "APPRECIATION",
  OFFER = "OFFER",
  APPRECIATION_DISTRIBUTE = "APPRECIATION_DISTRIBUTE",
  PUBLIC_APPRECIATION = "PUBLIC_APPRECIATION",
  FORMS = "FORMS",
  MESSAGE = "MESSAGE",
  USER_FEEDBACK = "USER_FEEDBACK",
  JOBYFINE_REQUEST = "JOBYFINE_REQUEST"
}

export interface IJobyfineStatesMail {
  state: string;
  subject: string;
  content: string;
  image: string;
  active?: boolean;
}

export interface IApiKey {
  name: string;
  permissions: EEntity[];
  key: string;
  createdAt: Date;
}

export interface IVendorLinks extends IBase {
  order?: number | null | undefined;
  name: string;
  icon: string;
  url: string;
  isDisabled?: boolean;
}

export interface IVendor extends IBase {
  slug: string;
  name: string;
  phone?: string;
  email?: string;
  status: string;
  logo?: string;
  loginBackgroundImage?: string;
  banners?: IVendorBanner[];
  slogan?: string;
  description?: string;
  address?: IVendorAddress;
  enableActionPlans?: boolean;
  enableCareerPlan?: boolean;
  enableChallenges?: boolean;
  enableAnonymousBox?: boolean;
  enableCoins?: boolean;
  enableJobyfine?: boolean;
  enableBukIntegration?: boolean;
  enableAdSync?: boolean;
  enableFlokzu?: boolean;
  enableExplore?: boolean;
  enableMagazine?: boolean;
  enableBuySell?: boolean;
  magazineName?: string;
  enableMicrosoftLogin?: boolean;
  enableLoginWithLinkedin?: boolean;
  jobyfineLinkedinSignupEnabled?: boolean;
  enableForms?: boolean;
  enableVacations?: boolean;
  jobyfineHiringManagers?: string[];
  jobyfineNotificationEmails?: string[];
  jobyfinePortalSlogan?: string;
  jobyfinePortalDescription?: string;
  jobyfinePortalTitle?: string;
  jobyfineResumeBanner?: string;
  domains?: string[];
  jobyfinePortalUrl?: string;
  apiKeys?: IApiKey[];
  emailDomains?: string[];
  clientId?: string;
  tenantId?: string;
  clientSecret?: string;
  onlyAdLogin?: boolean;
  identifier?: string;
  happyBirthdayPrompt?: string;
  happyBirthdayPromptText?: string;
  anniversaryPrompt?: string;
  anniversaryPromptText?: string;
  newUsersPrompt?: string;
  newUsersPromptText?: string;
  certificateUserPrompt?: string;
  certificateUserPromptText?: string;
  enableBirthdayAndAnniversary?: boolean;
  enableGoals?: boolean;
  enableCorporateValue?: boolean;
  enableUrlOnMails?: boolean;
  vendorLinks?: IVendorLinks[];
  corporateValuesMaxRecognitions?: number;
  usersWithoutRecognitionRestrictions?: string[];
  jobyfineNotifyOnRecruiterAssign?: string[];
  jobyfineStatesMail?: IJobyfineStatesMail[];
  isFullAccessEnabled?: boolean;
  enableCertificates?: boolean;
  enablePerformanceEvaluation?: boolean;
  activePlan?: string;
  enableBenefits?: boolean;
  benefitsPortalHeader?: string;
  benefitsPortalSubtitle?: string;
  benefitsPortalAnnouncements?: IBenefitsPortalAnnouncement[];
}

export enum BenefitsPortalAnnouncementKind {
  NEWS = "NEWS",
  POLICY = "POLICY",
  BROCHURE = "BROCHURE",
}

export interface IBenefitsPortalAnnouncement {
  title: string;
  url: string;
  kind: BenefitsPortalAnnouncementKind;
  country?: string;
}

export enum EClientStatusEnum {
  ACTIVE = 1,
  INACTIVE = 2
}

export interface IVendorClient extends IBase {
  name: string;
  externalId: string;
  status: EClientStatusEnum;
  vendor: string;
  identifier: string;
  clientCode: string;
  country?: string;
  countryName?: string;
}

export interface IVendorAddress {
  address: string;
  lat: number;
  lng: number;
}

export enum ECompanySegment {
  SOFTWARE = "SOFTWARE",
  HEALTH = "HEALTH",
  TECHSUP = "TECHSUP",
  EDUCATION = "EDUCATION",
  REAL_STATE = "REAL_STATE",
  FINANCIAL = "FINANCIAL",
  IT_SERVICE = "IT_SERVICE",
  RETAUIRE = "RETAUIRE",
  OTHER = "OTHER"
}

export interface IVendorBanner {
  title?: string;
  description?: string;
  bannerUrl?: string;
  cta?: string;
  btnColor?: string;
  btnText?: string;
  align?: string;
  modal?: string;
}

type ICompanySegment = {
  [_key in ECompanySegment]: { icon: IconProp; name: ECompanySegment };
};

export const companySegment: ICompanySegment = {
  [ECompanySegment.SOFTWARE]: { icon: "list", name: ECompanySegment.SOFTWARE },
  [ECompanySegment.HEALTH]: {
    icon: "kit-medical",
    name: ECompanySegment.HEALTH
  },
  [ECompanySegment.TECHSUP]: {
    icon: "seedling",
    name: ECompanySegment.TECHSUP
  },
  [ECompanySegment.EDUCATION]: {
    icon: "school",
    name: ECompanySegment.EDUCATION
  },
  [ECompanySegment.REAL_STATE]: {
    icon: "building",
    name: ECompanySegment.REAL_STATE
  },
  [ECompanySegment.FINANCIAL]: {
    icon: "credit-card",
    name: ECompanySegment.FINANCIAL
  },
  [ECompanySegment.IT_SERVICE]: {
    icon: "mobile",
    name: ECompanySegment.IT_SERVICE
  },
  [ECompanySegment.RETAUIRE]: { icon: "box", name: ECompanySegment.RETAUIRE },
  [ECompanySegment.OTHER]: { icon: "cog", name: ECompanySegment.OTHER }
};

export interface ISeniorityStats {
  seniority: EUserSeniority;
  quantity: number;
  users: IUser[];
}

/** Filter for vendorUsersCertificationsPaginated (back: VendorUserCertificationsInput.expiryStatus). */
export type VendorCertificationExpiryStatus =
  | "active"
  | "expired"
  | "expiringSoon";

export interface IVendorUserCertifications {
  user: IUser;
  quantity: number;
  quantityOutOfDate: number;
  /** Certs with issueDate in (now, now+30d]. Back may omit until merged. */
  quantityExpiringSoon?: number;
  lastUpload: Date;
  unix: number;
}

/** Global vendor cert expiry counters (not page-scoped). */
export interface IVendorCertificationExpiryStats {
  active: number;
  expired: number;
  expiringSoon: number;
}
